import React, { useRef, useState } from 'react';
import { DraggableModal } from '../../components/ui/DraggableModal';
import devopsBoardService from '../../services/devopsBoard.service';

interface Props {
  iterationPath: string;
  parent?: { id: number; title: string };
  onClose: () => void;
  onCreated: () => void;
}

type Result = { id: number } | { error: true };

const BulkCreateWorkItemsModal: React.FC<Props> = ({ iterationPath, parent, onClose, onCreated }) => {
  const singular = parent ? 'task' : 'story';
  const plural = parent ? 'tasks' : 'stories';
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [started, setStarted] = useState(false);
  const [results, setResults] = useState<Result[]>([]);
  const startedRef = useRef(false);
  const titles = text.split(/\r\n|\n|\r/).map(line => line.trim()).filter(Boolean);
  const tooLong = titles.some(title => Array.from(title).length > 255);
  const createdCount = results.filter(result => 'id' in result).length;

  const create = async () => {
    // Guard synchronously, including rapid clicks before React renders disabled state.
    if (startedRef.current || !preview || !titles.length || tooLong) return;
    startedRef.current = true;
    setStarted(true);
    setSaving(true);
    for (const title of titles) {
      try {
        const item = await devopsBoardService.createWorkItem({
          workItemType: parent ? 'Task' : 'User Story', title, iterationPath,
          ...(parent ? { parentId: parent.id } : {}),
        });
        setResults(previous => [...previous, { id: item.id }]);
      } catch {
        // A lost response can hide a successful write. Never automatically retry it.
        setResults(previous => [...previous, { error: true }]);
      }
    }
    setSaving(false);
    onCreated();
  };

  return (
    <DraggableModal isOpen onClose={() => { if (!saving) onClose(); }}
      title={preview ? `Preview ${parent ? 'tasks' : 'user stories'}` : parent ? 'Bulk add tasks' : 'Bulk create stories'} width={640}>
      <div className="space-y-4 p-4 text-sm text-gray-900 dark:text-gray-100">
        <p>Creates unassigned {parent ? 'Tasks' : 'User Stories'} in <strong>{iterationPath}</strong>. Each {singular} starts in New.</p>
        {parent && <p>Under <strong>#{parent.id} — {parent.title}</strong></p>}
        {!preview ? (
          <div>
            <label htmlFor="bulk-item-titles" className="mb-1 block font-medium">{parent ? 'Task titles' : 'Story titles'}</label>
            <p className="mb-2 text-xs text-muted">Paste one title per line. Blank lines are ignored.</p>
            <textarea id="bulk-item-titles" autoFocus rows={10} value={text}
              onChange={event => setText(event.target.value)}
              className="w-full rounded border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-900"
              placeholder={parent ? 'Build endpoint\nAdd search field' : 'Add shipment search\nShow delivery notifications'} />
            <p className="text-xs text-muted">{titles.length} {plural}</p>
            {tooLong && <p role="alert" className="text-red-600 dark:text-red-400">Each title must be 255 characters or fewer.</p>}
          </div>
        ) : (
          <>
            <p>{titles.length} {plural} to create</p>
            <ol className="max-h-80 list-decimal space-y-2 overflow-y-auto pl-7">
              {titles.map((title, index) => {
                const result = results[index];
                return <li key={index} className="break-words pr-2">
                  <span>{title}</span>
                  {result && ('id' in result
                    ? <span className="ml-2 text-green-700 dark:text-green-400">Created #{result.id}</span>
                    : <span className="ml-2 text-red-600 dark:text-red-400">Creation not confirmed. Check the board before trying again.</span>)}
                </li>;
              })}
            </ol>
            {started && <p role="status">{saving
              ? `Creating… ${results.length} of ${titles.length} processed.`
              : `${createdCount} of ${titles.length} ${plural} created.`}</p>}
          </>
        )}
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
          <button disabled={saving} onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1 dark:border-gray-600 disabled:opacity-50">
            {started ? 'Close' : 'Cancel'}
          </button>
          {preview && !started && <button onClick={() => setPreview(false)}
            className="rounded border border-gray-300 px-3 py-1 dark:border-gray-600">Edit list</button>}
          {!started && <button disabled={!titles.length || tooLong || !iterationPath}
            onClick={() => { if (preview) void create(); else setPreview(true); }}
            className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
            {preview ? `Create ${titles.length} ${titles.length === 1 ? singular : plural}` : 'Preview'}
          </button>}
        </div>
      </div>
    </DraggableModal>
  );
};

export default BulkCreateWorkItemsModal;
